import * as React from 'react'
import { Loader2, X } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { CHAT_EVAL_MAX_TURNS, CHAT_EVAL_STOP_TOKEN } from '@/config/constants'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { chameleon } from '@/lib/chameleon'

import { DEFAULT_EVAL_MODEL } from '../evalModels'
import { useCreateEvalCaseMutation } from '../hooks/useCreateEvalCaseMutation'

type CreateChatCaseModalProps = {
  dutyId: string
  onClose: () => void
}

export function CreateChatCaseModal({
  dutyId,
  onClose,
}: CreateChatCaseModalProps) {
  const [name, setName] = React.useState('')
  const [personaPrompt, setPersonaPrompt] = React.useState('')
  const [model, setModel] = React.useState(DEFAULT_EVAL_MODEL.model)
  const [provider, setProvider] = React.useState(DEFAULT_EVAL_MODEL.provider)
  const [dutyModel, setDutyModel] = React.useState('')
  const [dutyProvider, setDutyProvider] = React.useState('')

  const { toTask } = useAppNavigate()
  const createMutation = useCreateEvalCaseMutation()

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (next) return
      if (createMutation.isPending) {
        toast.info('Creating test case… hang tight.')
        return
      }
      onClose()
    },
    [onClose, createMutation.isPending]
  )

  const handleSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      const trimmedName = name.trim()
      const trimmedPrompt = personaPrompt.trim()
      const trimmedModel = model.trim()
      const trimmedProvider = provider.trim()
      if (
        !trimmedName ||
        !trimmedPrompt ||
        !trimmedModel ||
        !trimmedProvider ||
        createMutation.isPending
      )
        return

      createMutation.mutate(
        {
          caseInput: {
            name: trimmedName,
            duty_id: dutyId,
            channel: 'chat',
            system_prompt: trimmedPrompt,
            model: trimmedModel,
            provider: trimmedProvider,
            duty_model: dutyModel.trim() || undefined,
            duty_provider: dutyProvider.trim() || undefined,
          },
        },
        {
          onSuccess: ({ created }) => {
            // Close first, then navigate — navigating doesn't unmount this modal
            // (its parent stays mounted), so without onClose() the Dialog lingers
            // over the detail page. Mirrors CreateDutyModal.
            onClose()
            toast.success(`Created "${trimmedName}".`)
            chameleon.track('Eval Case Created')
            if (created?.id) {
              toTask(`/evals/${dutyId}/test-cases/${created.id}`, {
                replace: false,
                state: { caseName: trimmedName },
              })
            }
          },
          onError: (error: Error) => {
            toast.error(error.message || 'Failed to create test case')
          },
        }
      )
    },
    [
      name,
      personaPrompt,
      model,
      provider,
      dutyModel,
      dutyProvider,
      dutyId,
      createMutation,
      toTask,
      onClose,
    ]
  )

  const isSubmitDisabled =
    !name.trim() ||
    !personaPrompt.trim() ||
    !model.trim() ||
    !provider.trim() ||
    createMutation.isPending

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg" showCloseButton={false}>
        <div className="flex items-center justify-between">
          <DialogTitle>New chat test case</DialogTitle>
          <DialogDescription className="sr-only">
            Create a chat eval test case: describe the counterparty prompt and
            pick its model.
          </DialogDescription>
          <DialogClose asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close"
              className="text-muted-foreground hover:text-foreground">
              <X className="size-4" />
            </Button>
          </DialogClose>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="case-name" className="font-medium">
              Name
            </Label>
            <Input
              id="case-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Appointment reschedule"
              disabled={createMutation.isPending}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="case-persona" className="font-medium">
              Counterparty prompt
            </Label>
            <Textarea
              id="case-persona"
              value={personaPrompt}
              onChange={(e) => setPersonaPrompt(e.target.value)}
              placeholder="You are a patient who wants to reschedule next week's appointment. Be brief, a little impatient, and don't volunteer your date of birth unless asked."
              rows={4}
              disabled={createMutation.isPending}
            />
            <p className="text-xs text-neutral-low-contrast">
              Give it a clear goal so it ends with{' '}
              <code className="rounded bg-gray-100 px-1 font-mono text-[11px] text-primary-high-contrast">
                {CHAT_EVAL_STOP_TOKEN}
              </code>
              , otherwise it runs {CHAT_EVAL_MAX_TURNS} turns.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="case-model" className="font-medium">
                Counterparty model
              </Label>
              <Input
                id="case-model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="e.g. gpt-4o-mini"
                disabled={createMutation.isPending}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="case-provider" className="font-medium">
                Counterparty provider
              </Label>
              <Input
                id="case-provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                placeholder="e.g. openai"
                disabled={createMutation.isPending}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="case-duty-model"
                className="font-medium text-neutral-low-contrast">
                Duty agent model
                <span className="ml-1 font-normal">(optional)</span>
              </Label>
              <Input
                id="case-duty-model"
                value={dutyModel}
                onChange={(e) => setDutyModel(e.target.value)}
                placeholder="Duty default"
                disabled={createMutation.isPending}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="case-duty-provider"
                className="font-medium text-neutral-low-contrast">
                Duty agent provider
                <span className="ml-1 font-normal">(optional)</span>
              </Label>
              <Input
                id="case-duty-provider"
                value={dutyProvider}
                onChange={(e) => setDutyProvider(e.target.value)}
                placeholder="Duty default"
                disabled={createMutation.isPending}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={isSubmitDisabled}>
              {createMutation.isPending && (
                <Loader2 className="size-4 animate-spin" />
              )}
              Create test case
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
